/**
 * Dashboard Persistence API
 */
import { api } from '@/lib/api';
import { edenError } from '@solidafy/api-client';
import type { DashboardListItem, DashboardFull, UpdateDashboardBody } from '@solidafy/api-client';
import type { DashboardSpec } from '@/types/dashboard';

export async function listDashboards(): Promise<DashboardListItem[]> {
  const { data, error } = await api.api.v1.bi.dashboards.get();
  if (error) throw new Error(edenError(error, 'Failed to list dashboards'));
  return data;
}

export async function getDashboard(id: string): Promise<DashboardFull> {
  const { data, error } = await api.api.v1.bi.dashboards({ id }).get();
  if (error) throw new Error(edenError(error, 'Failed to get dashboard'));
  return data;
}

export async function createDashboard(request: {
  name: string;
  description?: string;
  spec: DashboardSpec;
}): Promise<DashboardFull> {
  const { data, error } = await api.api.v1.bi.dashboards.post({
    name: request.name,
    description: request.description,
    // DashboardSpec → Record<string, unknown> for the API
    spec: request.spec as unknown as Record<string, unknown>,
  });
  if (error) throw new Error(edenError(error, 'Failed to create dashboard'));
  if (!data || !('id' in data)) throw new Error('Failed to create dashboard');
  return data;
}

export async function updateDashboard(
  id: string,
  request: { name?: string; description?: string; spec?: DashboardSpec; is_published?: boolean },
): Promise<DashboardFull> {
  const body: UpdateDashboardBody = {
    name: request.name,
    description: request.description,
    is_published: request.is_published,
  };
  if (request.spec) {
    // DashboardSpec → Record<string, unknown> for the API
    body.spec = request.spec as unknown as Record<string, unknown>;
  }
  const { data, error } = await api.api.v1.bi.dashboards({ id }).put(body);
  if (error) throw new Error(edenError(error, 'Failed to update dashboard'));
  if (!data || !('id' in data)) throw new Error('Failed to update dashboard');
  return data;
}

export async function deleteDashboard(id: string): Promise<void> {
  const { error } = await api.api.v1.bi.dashboards({ id }).delete();
  if (error) throw new Error(edenError(error, 'Failed to delete dashboard'));
}
